import { useEffect, useMemo, useState } from 'react';
import { auditTrailApi, type PagedAuditTrailResponse } from '@/services/auditTrailApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { AuditTrailQuery } from '@/utils/AuditTrail/types';
import { PAGE_SIZE } from '@/utils/AuditTrail/constants';
import { mapAuditTrailRow } from '@/utils/AuditTrail/mappers';

interface UseAuditTrailDataParams {
  canView: boolean;
  query: AuditTrailQuery;
  page: number;
  companyId: string | undefined;
}

export function useAuditTrailData({ canView, query, page, companyId }: UseAuditTrailDataParams) {
  const [result, setResult] = useState<PagedAuditTrailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    auditTrailApi
      .getAuditTrail({ ...query, pageNumber: page, pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setAccessDenied(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (getApiErrorStatus(err) === 403) setAccessDenied(true);
        else setLoadError(err instanceof Error ? err.message : 'Failed to load the audit trail');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [canView, query, page, companyId]);

  const rows = useMemo(() => (result?.items ?? []).map(mapAuditTrailRow), [result]);

  return { result, rows, isLoading, loadError, accessDenied };
}
